import React, { useEffect, useState, useMemo, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Paperclip, FileWarning, RotateCcw, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { useStickyState, useResetStickyState } from "@/hooks/useStickyState";
import { NewJournalEntryDialog } from "@/components/NewJournalEntryDialog";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { ListSearch, matchesSearch } from "@/components/ListSearch";
import { DefaultFilterPin, useDefaultFilter } from "@/components/DefaultFilterPin";
import { SwissDateInput } from "@/components/SwissDateInput";

// Welche document_types gelten als "mit Beleg" (= mit verknüpftem Dokument)?
// "manual" ohne document_id = reine Handbuchung ohne Beleg.
const DOC_TYPES_WITH_DOCUMENT = new Set([
  "receipt",
  "invoice",
  "invoice_payment",
  "bill",
  "bill_payment",
  "bank_statement",
]);

function hasAttachedDocument(entry) {
  if (!entry) return false;
  if (entry.document_id) return true;
  return DOC_TYPES_WITH_DOCUMENT.has(entry.document_type);
}

export function Journal() {
  const companyId = localStorage.getItem("demoCompanyId");
  const [entries, setEntries] = useState([]);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  useEffect(() => {
    if (!highlightId || !entries.length) return;
    const t = setTimeout(() => document.querySelector(`[data-testid="journal-row-${highlightId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    return () => clearTimeout(t);
  }, [highlightId, entries]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useStickyState("journal.startDate", "", companyId);
  const [endDate, setEndDate] = useStickyState("journal.endDate", "", companyId);
  const [docFilter, setDocFilter] = useStickyState("journal.docFilter", "all", companyId);
  const docFilterDef = useDefaultFilter("journal.docFilter", companyId, docFilter, setDocFilter);
  const resetJournalView = useResetStickyState("journal", companyId);

  const loadEntries = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await apiClient.get(`/companies/${companyId}/journal`, { params });
      setEntries(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const [lockedUntil, setLockedUntil] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/period-lock`)
      .then((r) => setLockedUntil(r.data?.locked_until || null))
      .catch(() => {});
  }, [companyId]);

  useEffect(() => {
    loadEntries();
  }, []);

  const reverseEntry = useCallback(
    async (entry) => {
      if (
        !window.confirm(
          `Buchung wirklich stornieren?\n"${entry.description}"\n\nEs wird eine Storno-Buchung mit getauschten Soll/Haben-Beträgen erzeugt; die Original-Buchung bleibt zur Nachvollziehbarkeit erhalten.`
        )
      ) {
        return;
      }
      try {
        await apiClient.post(`/journal-entries/${entry._id}/reverse`);
        toast.success("Storno-Buchung erstellt.");
        loadEntries();
      } catch (e) {
        const detail = e?.response?.data?.detail || "Storno fehlgeschlagen.";
        toast.error(String(detail));
      }
    },
    []
  );

  const deleteEntry = useCallback(async (entry) => {
    if (entry.status !== "draft") {
      toast.info("Verbuchte Buchungen werden nicht gelöscht (Revisionssicherheit). Bitte «Storno-Buchung erzeugen» verwenden und neu erfassen.", { duration: 6000 });
      return;
    }
    if (!window.confirm(`Entwurf wirklich löschen?\n"${entry.description}"`)) return;
    try {
      await apiClient.delete(`/journal-entries/${entry._id}`);
      toast.success("Entwurf gelöscht.");
      loadEntries();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Löschen fehlgeschlagen.";
      toast.error(String(detail));
    }
  }, []);

  const calcTotals = (lines) => {
    return lines.reduce(
      (acc, l) => {
        acc.debit += l.debit || 0;
        acc.credit += l.credit || 0;
        return acc;
      },
      { debit: 0, credit: 0 },
    );
  };

  const typeBadge = (type) => {
    const map = {
      invoice: { label: "Rechnung", className: "bg-sky-100 text-sky-800" },
      invoice_payment: { label: "Zahlung", className: "bg-emerald-100 text-emerald-800" },
      receipt: { label: "Beleg", className: "bg-slate-100 text-slate-700" },
    };
    const info = map[type] || { label: type, className: "bg-slate-100 text-slate-700" };
    return (
      <Badge className={info.className} data-testid={`journal-type-badge-${type}`}>
        {info.label}
      </Badge>
    );
  };

  const [search, setSearch] = useState("");
  const filteredEntries = useMemo(() => {
    const base = docFilter === "with" ? entries.filter(hasAttachedDocument) : docFilter === "without" ? entries.filter((e) => !hasAttachedDocument(e)) : entries;
    return base.filter((e) => matchesSearch(
      [e.description, e.date, e.status, e.reference, e.source_type, ...(e.lines || []).flatMap((l) => [l.account_id, l.description])],
      (e.lines || []).flatMap((l) => [l.debit, l.credit, l.amount]), search));
  }, [entries, docFilter, search]);

  const counts = useMemo(() => {
    let withDoc = 0;
    let withoutDoc = 0;
    for (const e of entries) {
      if (hasAttachedDocument(e)) withDoc++;
      else withoutDoc++;
    }
    return { total: entries.length, with: withDoc, without: withoutDoc };
  }, [entries]);

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Buchungen (Journal)
          </h1>
          {lockedUntil && (
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-slate-100 text-xs px-3 py-1" data-testid="journal-locked-badge">
              🔒 Definitiv gesperrt bis {lockedUntil} — Buchungen bis zu diesem Datum sind unveränderbar
            </p>
          )}
          <p className="mt-1 text-sm text-slate-500">
            Doppelbuchhaltung: alle Soll-/Haben-Buchungen im 
Überblick.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setNewOpen(true)} data-testid="journal-new-entry-btn">
            <Plus className="h-4 w-4 mr-1" /> Neue Buchung
          </Button>
        </div>
        <NewJournalEntryDialog companyId={companyId} open={newOpen} onOpenChange={setNewOpen} onCreated={loadEntries} />
        {(startDate || endDate || docFilter !== "all") && (
          <button
            type="button"
            onClick={() => { setStartDate(""); setEndDate(""); setDocFilter("all"); resetJournalView(); }}
            className="text-[11px] text-slate-500 hover:text-slate-700 underline mt-2"
            data-testid="journal-reset-view"
            title="Filter zurücksetzen"
          >
            ↻ Standard
          </button>
        )}
      </div>

      <Card
        className="p-4 border border-slate-200 bg-white shadow-sm"
        data-testid="journal-table-card"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Journal</h2>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <div className="hidden md:flex items-center gap-2">
              <span>Datum von</span>
              <SwissDateInput value={startDate} onChange={setStartDate} max={endDate || undefined} testid="journal-start-date" />
              <span>bis</span>
              <SwissDateInput value={endDate} onChange={setEndDate} min={startDate || undefined} testid="journal-end-date" />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full px-3"
                onClick={loadEntries}
                data-testid="journal-filter-apply"
              >
                Filter anwenden
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full px-3"
                onClick={async () => {
                  if (!companyId) return;
                  const params = new URLSearchParams();
                  if (startDate) params.append("start_date", startDate);
                  if (endDate) params.append("end_date", endDate);
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/journal-export?${params.toString()}`;
                  window.location.href = url;
                }}
                data-testid="journal-export-csv"
              >
                CSV exportieren
              </Button>
            </div>
            {loading && (
              <span
                className="text-xs text-slate-400"
                data-testid="journal-loading-indicator"
              >
                Lade ...
              </span>
            )}
          </div>
        </div>

        {/* Document-Filter pills */}
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <ListSearch value={search} onChange={setSearch} count={filteredEntries.length} testid="journal-search" className="order-last ml-auto w-full sm:w-72" placeholder="Suchen: Text, Konto, Betrag, Datum …" />
          <span className="text-xs text-slate-500 mr-1">Beleg-Status:</span>
          {[
            { key: "all", label: `Alle (${counts.total})` },
            { key: "with", label: `Mit Beleg (${counts.with})` },
            { key: "without", label: `Ohne Beleg (${counts.without})` },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setDocFilter(opt.key)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                docFilter === opt.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`journal-doc-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
          <DefaultFilterPin value={docFilter} label={{ all: "Alle", with: "Mit Beleg", without: "Ohne Beleg" }[docFilter] || docFilter}
            defaultValue={docFilterDef.defaultValue} isDefault={docFilterDef.isDefault} onSave={docFilterDef.save} testId="journal-default-filter-pin" />
        </div>

        <Table data-testid="journal-table">
          <TableHeader>
            <TableRow>
              <TableHead>Datum</TableHead>
              <TableHead>Beleg</TableHead>
              <TableHead className="w-12 text-center" title="Hat ein verknüpftes Dokument?">
                <Paperclip className="h-3.5 w-3.5 inline-block" />
              </TableHead>
              <TableHead>Typ</TableHead>
              <TableHead>Konten &amp; Beträge</TableHead>
              <TableHead className="text-right">Summe Soll/Haben</TableHead>
              <TableHead className="text-right w-32">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.length === 0 && (
              <TableRow data-testid="journal-empty-row">
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    testid="journal-empty-state"
                    icon={<BookOpen className="h-5 w-5" />}
                    title={
                      docFilter === "with"
                        ? "Keine Buchungen mit Beleg im Zeitraum"
                        : docFilter === "without"
                        ? "Alle Buchungen haben einen Beleg"
                        : "Noch keine Buchungen"
                    }
                    description={
                      docFilter === "without"
                        ? "Glückwunsch — lückenlose Buchhaltung!"
                        : "Verbuchte Belege, Rechnungen und Kassenbewegungen erscheinen automatisch im Journal."
                    }
                  />
                </TableCell>
              </TableRow>
            )}
            {filteredEntries.map((entry) => {
              const totals = calcTotals(entry.lines || []);
              const hasDoc = hasAttachedDocument(entry);
              return (
                <TableRow
                  key={entry._id}
                  className={highlightId === entry._id ? "bg-amber-50 ring-2 ring-inset ring-amber-300" : undefined}
                  data-highlighted={highlightId === entry._id ? "true" : undefined}
                  data-testid={`journal-row-${entry._id}`}
                >
                  <TableCell>{formatDate(entry.date)}</TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell className="text-center">
                    {hasDoc ? (
                      <span
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"
                        title={`Verknüpft mit ${entry.document_type}`}
                        data-testid={`journal-has-doc-${entry._id}`}
                      >
                        <Paperclip className="h-3 w-3" />
                      </span>
                    ) : (
                      <span
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-50 text-amber-700"
                        title="Reine Handbuchung ohne Beleg"
                        data-testid={`journal-no-doc-${entry._id}`}
                      >
                        <FileWarning className="h-3 w-3" />
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{typeBadge(entry.document_type)}</TableCell>
                  <TableCell>
                    <div className="space-y-1 text-xs">
                      {(entry.lines || []).map((l, idx) => (
                        <div
                          key={idx}
                          data-testid={`journal-line-${entry._id}-${idx}`}
                        >
                          <span className="font-mono text-slate-700">Kto {l.account_id}</span>
                          {" - "}
                          <span className="text-slate-500">
                            Soll {l.debit.toFixed(2)} / Haben {l.credit.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-xs text-slate-700">
                    <div data-testid={`journal-total-${entry._id}`}>
                      Soll {totals.debit.toFixed(2)} / Haben {totals.credit.toFixed(2)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!entry.reversal_of && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => reverseEntry(entry)}
                          title="Storno-Buchung erzeugen"
                          data-testid={`journal-reverse-${entry._id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {entry.status === "draft" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => deleteEntry(entry)}
                          title="Entwurf löschen"
                          data-testid={`journal-delete-${entry._id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <span className="h-7 w-7 inline-flex items-center justify-center text-slate-300" title="Verbuchte Buchungen sind unveränderbar – Korrektur nur per Storno-Buchung" data-testid={`journal-locked-${entry._id}`}>
                          <Lock className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
